import React, { Fragment } from "react";
import { ScaleLinear } from "@nivo/scales";
import { computeXYScalesForSeries } from "@nivo/scales";
import { useTooltip, TableTooltip } from "@nivo/tooltip";
import { Axes } from "@nivo/axes";
import { line } from "d3-shape";
import { BarChartFormattedData } from "../../models/controlPanelDataModel";
import { graphPaletteV2 } from "../../styles";

export const PathwayDotsLayer: React.FC<LineProps> = ({
  bars,
  xScale,
  innerWidth,
  innerHeight,
  data,
}) => {
  const maxValue = Math.max(...data.map((item) => item.v1));

  const scale = computeXYScalesForSeries(
    [
      {
        id: "only",
        //eslint-disable-next-line
        // @ts-ignore
        data: data.map((it) => ({ x: it.d, y: it.v1 })),
      },
    ],
    { type: "linear" },
    {
      type: "linear",
      min: 0, // Ensure y-scale starts at 0
      max: maxValue * 2, // Set max to double the maximum y-value found
    },
    innerWidth,
    innerHeight,
  );

  const lineGenerator = line()
    //eslint-disable-next-line
    // @ts-ignore
    .x((bar: Bar) => bar.x + bar.width / 2)
    //eslint-disable-next-line
    // @ts-ignore
    .y((bar: Bar) => {
      const yValue = scale.yScale(bar.data.data.v1);
      return typeof yValue === "number" ? yValue : 0;
    });

  const tip = useTooltip();

  const renderTip = (
    e: React.MouseEvent<SVGRectElement, MouseEvent>,
    idx: number,
  ): void => {
    return tip.showTooltipFromEvent(
      <CustomTooltip barValue={data[idx].v} lineValue={data[idx].v1} />,
      e,
    );
  };

  return (
    <Fragment>
      <Axes
        yScale={scale.yScale}
        xScale={xScale}
        width={innerWidth}
        height={innerHeight}
        right={{
          ticksPosition: "after",
          format: (value) => `-${value}%`,
        }}
      />
      <path
        d={lineGenerator(bars) || ""}
        fill="none"
        stroke="transparent"
        style={{ pointerEvents: "none" }}
      />
      {bars.map((bar) => (
        <circle
          key={bar.key}
          cx={bar.x + bar.width / 2}
          cy={scale.yScale(bar.data.data.v1)}
          r={4}
          fill={graphPaletteV2[6]}
          stroke={graphPaletteV2[6]}
          style={{ pointerEvents: "none" }}
        />
      ))}
      {bars.map((bar, idx) => (
        <rect
          key={bar.key}
          x={bar.x}
          y={0}
          height={innerHeight}
          width={bar.width}
          fill="transparent"
          onMouseEnter={(e): void => renderTip(e, idx)}
          onMouseMove={(e): void => renderTip(e, idx)}
          onMouseLeave={tip.hideTooltip}
        />
      ))}
    </Fragment>
  );
};

const CustomTooltip = ({
  barValue,
  lineValue,
}: {
  barValue: number;
  lineValue: number;
}): JSX.Element => {
  return (
    <TableTooltip
      rows={[
        [
          "Net Negative Investments",
          <b key="net-negative-investments">{barValue.toFixed(2)}</b>,
        ],
        [
          "Relative Decarbonisation",
          <b key="relative decarbonisation">{`${lineValue.toFixed(2)}%`}</b>,
        ],
      ]}
    />
  );
};

interface LineProps {
  bars: any[]; //eslint-disable-line
  xScale: ScaleLinear<number>;
  innerWidth: number;
  innerHeight: number;
  data: BarChartFormattedData[];
}
